import React from 'react';
import { 
  XCircleIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  BriefcaseIcon, 
  CalendarIcon, 
  CurrencyDollarIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  IdentificationIcon,
  PencilSquareIcon // Добавили иконку для редактирования
} from '@heroicons/react/24/outline';

const EmployeeDetailsModal = ({ employee, onClose, onEdit }) => { // Принимаем onEdit
  if (!employee) return null;

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-[40px] w-full max-w-xl shadow-2xl border border-zinc-100 overflow-hidden animate-in fade-in zoom-in duration-300">
        
        {/* Header with Profile Header */}
        <div className="relative h-32 bg-gradient-to-r from-orange-500 to-rose-500">
          <div className="absolute top-6 right-6 flex items-center gap-3">
            {/* КНОПКА ИЗМЕНИТЬ */}
            <button
              onClick={onEdit}
              className="flex items-center px-4 py-2 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-2xl transition-all text-white border border-white/20 text-xs font-bold"
            >
              <PencilSquareIcon className="h-4 w-4 mr-2" />
              Изменить
            </button>

            {/* КНОПКА ЗАКРЫТЬ */}
            <button
              onClick={onClose}
              className="p-2 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full transition-all text-white border border-white/20"
            >
              <XCircleIcon className="h-7 w-7" />
            </button>
          </div>
          
          <div className="absolute -bottom-12 left-10">
            <div className="h-24 w-24 rounded-[30px] bg-white p-1.5 shadow-xl">
              <div className="h-full w-full rounded-[24px] bg-zinc-100 flex items-center justify-center overflow-hidden">
                <UserCircleIcon className="h-16 w-16 text-zinc-300" />
              </div>
            </div>
          </div>
        </div>

        <div className="pt-16 p-10">
          {/* Main Info */}
          <div className="mb-8">
            <h3 className="text-3xl font-black text-zinc-900 tracking-tight">
              {employee.first_name} {employee.last_name}
            </h3>
            <div className="flex items-center mt-2 text-zinc-500 font-medium">
              <span className="bg-zinc-100 px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider mr-3">
                @{employee.username}
              </span>
              <div className={`flex items-center text-xs font-bold uppercase tracking-widest ${employee.is_active ? 'text-emerald-500' : 'text-rose-500'}`}>
                <div className={`h-2 w-2 rounded-full mr-2 ${employee.is_active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                {employee.is_active ? 'Online' : 'Offline'}
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-6">
            
            {/* Contact Info */}
            <div className="space-y-4">
              <DetailItem 
                icon={<EnvelopeIcon />} 
                label="Email" 
                value={employee.email} 
              />
              <DetailItem 
                icon={<PhoneIcon />} 
                label="Телефон" 
                value={employee.phone || 'Не указан'} 
              />
              <DetailItem 
                icon={<BriefcaseIcon />} 
                label="Позиция / Отдел" 
                value={`${employee.role} • ${employee.department}`} 
                capitalize 
              />
            </div>

            {/* Work Info */}
            <div className="space-y-4">
              <DetailItem 
                icon={<CurrencyDollarIcon />} 
                label="Оклад" 
                value={employee.salary ? `$${Number(employee.salary).toLocaleString()}` : 'Не установлен'} 
                highlight
              />
              <DetailItem 
                icon={<CalendarIcon />} 
                label="Дата найма" 
                value={formatDate(employee.hire_date)} 
              />
              <DetailItem 
                icon={<IdentificationIcon />} 
                label="ID сотрудника" 
                value={`#${employee.id.toString().slice(-6).toUpperCase()}`} 
              />
            </div>
          </div>

          {/* Permissions Section */}
          <div className="mt-8 pt-8 border-t border-zinc-100">
            <div className="flex items-center mb-4 text-zinc-400">
              <ShieldCheckIcon className="h-5 w-5 mr-2" />
              <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Доступы и права</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {employee.permissions && employee.permissions.length > 0 ? (
                employee.permissions.map((perm, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-zinc-50 text-zinc-600 text-[11px] font-bold rounded-xl border border-zinc-100">
                    {perm}
                  </span>
                ))
              ) : (
                <span className="text-zinc-400 text-xs italic">Права не назначены</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailItem = ({ icon, label, value, capitalize, highlight }) => (
  <div className="flex flex-col">
    <div className="flex items-center text-zinc-400 mb-1">
      {React.cloneElement(icon, { className: "h-3.5 w-3.5 mr-1.5" })}
      <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
    </div>
    <span className={`text-sm font-bold ${highlight ? 'text-orange-600' : 'text-zinc-700'} ${capitalize ? 'capitalize' : ''}`}>
      {value}
    </span>
  </div>
);

export default EmployeeDetailsModal;